<ng-container>
  <form [formGroup]="meetingForm" class="meeting-form" (ngSubmit)="onSubmit()">
    <app-dialog-header title="Meeting" (closeClicked)="onCancel()">
    </app-dialog-header>

    <mat-dialog-content class="fullscreen-dialog">
      <div class="container">
        <!-- All existing form fields except the meetingSummary -->
        <div class="form-row">
          <!-- Description Field -->
          <mat-form-field appearance="fill" class="full-width">
            <mat-label>Description</mat-label>
            <input matInput formControlName="description" placeholder="Enter meeting description" />
          </mat-form-field>
        </div>
        <!-- Visit Type -->
        <div class="form-row">
          <mat-form-field appearance="fill" class="form-field">
            <mat-label>Visit Type</mat-label>
            <mat-select formControlName="visitTypeId">
              @for (visitType of visitTypes; track visitType) {
              <mat-option [value]="visitType.id">
                {{ visitType.description }}
              </mat-option>
              }
            </mat-select>
          </mat-form-field>

          <!-- Recurrence Type -->
          <mat-form-field appearance="fill" class="form-field">
            <mat-label>Recurrence Type</mat-label>
            <mat-select formControlName="recurrenceType">
              <mat-option value="Once">Once</mat-option>
              <mat-option value="Weekly">Weekly</mat-option>
              <mat-option value="Custom">Custom</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <!-- Date and Time Fields -->
        <div class="form-row">
          <!-- Start Date -->
          <mat-form-field appearance="fill" class="form-field">
            <mat-label>Start Date</mat-label>
            <input matInput [matDatepicker]="startPicker" formControlName="startDate" [min]="minDate"
              aria-label="Start date" title="Select start date" />
            <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
            <mat-datepicker #startPicker></mat-datepicker>
          </mat-form-field>

          <!-- End Date Field -->
          @if (showEndDate) {
          <mat-form-field appearance="fill" class="form-field">
            <mat-label>End Date</mat-label>
            <input matInput [matDatepicker]="endPicker" formControlName="endDate" [min]="minDate" aria-label="End date"
              title="Select end date" />
            <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
            <mat-datepicker #endPicker></mat-datepicker>
          </mat-form-field>
          }
        </div>
        <div class="form-row">
          <!-- Start Time -->
          <mat-form-field appearance="fill" class="form-field time-field">
            <mat-label>Start Time</mat-label>
            <input matInput type="time" formControlName="startTime" class="time-input" aria-label="Start time"
              title="Select start time" />
          </mat-form-field>
          <!-- Duration -->
          <mat-form-field appearance="fill" class="form-field time-field">
            <mat-label>Duration (minutes)</mat-label>
            <input matInput type="number" formControlName="duration" min="1" class="duration-input"
              aria-label="Duration in minutes" title="Enter duration in minutes" />
          </mat-form-field>
        </div>
        <!-- Weekdays Checkboxes -->
        @if (showWeekdays) {
        <div class="weekdays">
          <mat-checkbox formControlName="monday">Monday</mat-checkbox>
          <mat-checkbox formControlName="tuesday">Tuesday</mat-checkbox>
          <mat-checkbox formControlName="wednesday">Wednesday</mat-checkbox>
          <mat-checkbox formControlName="thursday">Thursday</mat-checkbox>
          <mat-checkbox formControlName="friday">Friday</mat-checkbox>
          <mat-checkbox formControlName="saturday">Saturday</mat-checkbox>
          <mat-checkbox formControlName="sunday">Sunday</mat-checkbox>
        </div>
        }
        <!-- Every X Days Field -->
        @if (showEveryXDays) {
        <div class="form-row">
          <mat-form-field appearance="fill" class="form-field">
            <mat-label>Every X Days</mat-label>
            <input matInput type="number" formControlName="everyXDays" min="1" aria-label="Repeat every X days"
              title="Enter number of days between repeats" />
          </mat-form-field>
        </div>
        }

        <!-- Resource Selection -->
        <div class="form-row">
          <mat-form-field class="full-width" appearance="fill">
            <mat-label>Resources</mat-label>
            <mat-chip-grid #chipGrid aria-label="Resource selection">
              @for (resourceEntry of selectedResources; track resourceEntry) {
              <mat-chip-row [removable]="true" (removed)="removeResource(resourceEntry)" [ngClass]="{
                  'chip-available': resourceEntry.status === 'Available',
                  'chip-conflicts': resourceEntry.status === 'Conflicts',
                  'chip-error': resourceEntry.status === 'Error',
                  'chip-checking': resourceEntry.status === 'Checking'
                }"
                [matTooltip]="resourceEntry.conflicts.length > 0 ? 'Conflicts: ' + resourceEntry.conflicts.join(', ') : ''">
                <!-- Add avatar image -->
                @if (resourceEntry.resource.imageUrl) {
                <img [src]="resourceEntry.resource.imageUrl" alt="{{ resourceEntry.resource.name }}"
                  class="avatar-image" />
                }
                <div class="chip-content">
                  <span class="resource-name">{{ resourceEntry.resource.name }}</span>
                  <div class="chip-status">
                    @if (resourceEntry.loading) {
                    <mat-spinner diameter="16"></mat-spinner>
                    }
                    @if (!resourceEntry.loading) {
                    <span>
                      {{ resourceEntry.status }}
                    </span>
                    }
                  </div>
                </div>
                <button matChipRemove aria-label="Remove resource">
                  <mat-icon>cancel</mat-icon>
                </button>
              </mat-chip-row>
              }
              <input #resourceInput placeholder="Add Resource..." [matChipInputFor]="chipGrid"
                [matChipInputSeparatorKeyCodes]="separatorKeysCodes" [matChipInputAddOnBlur]="false"
                [matAutocomplete]="auto" formControlName="resourceCtrl" (matChipInputTokenEnd)="addResource($event)"
                aria-label="Add resource" />
            </mat-chip-grid>
            <mat-autocomplete #auto="matAutocomplete" (optionSelected)="selectedResource($event)">
              @for (resource of filteredResources | async; track resource) {
              <mat-option [value]="resource">
                {{ resource.name }}
              </mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        </div>

        <!-- Location Selection -->
        <div class="form-row">
          <mat-form-field class="full-width" appearance="fill">
            <mat-label>Location</mat-label>
            <input matInput [value]="locationDisplay" readonly (click)="openLocationChooser()"
              placeholder="Select Location" />
            @if (locationDisplay) {
            <button mat-icon-button matSuffix (click)="clearLocation()">
              <mat-icon>clear</mat-icon>
            </button>
            }
          </mat-form-field>
        </div>

        <!-- If URM is enabled and we have an externalPropertyReference, show property info -->
        @if (urmEnabled && meetingForm.get('externalPropertyReference').value) {
        <div>
          <urm-property-info
            [externalPropertyReference]="meetingForm.get('externalPropertyReference').value"></urm-property-info>
        </div>
        }
        <!-- Remove Meeting Summary from here -->
      </div>
    </mat-dialog-content>

    <!-- Move Meeting Summary to mat-dialog-actions -->
    <mat-dialog-actions class="dialog-actions" align="end">
      <span class="meeting-summary">{{ meetingSummary }}</span>
      <button mat-flat-button color="primary" type="submit">Submit</button>
    </mat-dialog-actions>
  </form>
</ng-container>